import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { alpha } from '@mui/material/styles';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import MuiLink from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import {
  ArrowRight,
  CheckCircle2,
  CreditCard,
  ExternalLink,
  HelpCircle,
  ReceiptText,
  ShieldCheck,
  WalletCards,
} from 'lucide-react';
import useProfileOverview from '../hooks/useProfileOverview';
import { billingAPI } from '../services/api';
import { marketingPalette, marketingSymbols } from '../content/marketing';
import {
  publicBodyFontFamily,
  publicHeadingFontFamily,
  publicPrimaryButtonSx,
  publicSecondaryButtonSx,
} from '../components/common/PublicPageFrame';

const palette = marketingPalette;

const surfaceSx = {
  position: 'relative',
  overflow: 'hidden',
  borderRadius: { xs: '26px', md: '30px' },
  border: `1px solid ${alpha(palette.ink, 0.08)}`,
  bgcolor: alpha('#ffffff', 0.94),
  boxShadow: '0 28px 72px rgba(18, 61, 103, 0.08)',
};

const insetSx = {
  borderRadius: '22px',
  border: `1px solid ${alpha(palette.ink, 0.08)}`,
  bgcolor: alpha(palette.heroBlueMist, 0.58),
};

const statusChipSx = {
  borderRadius: '999px',
  fontWeight: 800,
  fontFamily: publicBodyFontFamily,
  textTransform: 'capitalize',
};

const formatDateTime = (value) => {
  if (!value) {
    return 'Not available';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return 'Not available';
  }

  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatAverageScore = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric.toFixed(1) : 'Not available';
};

const formatCurrency = (amountCents, currency = 'GBP') => {
  const amount = Number(amountCents || 0) / 100;
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
    }).format(amount);
  } catch {
    return `${amount.toFixed(2)} ${currency}`;
  }
};

const pluralise = (value, singular, plural = `${singular}s`) => (
  Number(value) === 1 ? singular : plural
);

const getCheckoutAlert = (searchParams) => {
  const rawStatus = (
    searchParams.get('checkout')
    || searchParams.get('billing')
    || searchParams.get('status')
    || ''
  ).trim().toLowerCase();

  if (['success', 'complete', 'completed', 'paid'].includes(rawStatus)) {
    return {
      severity: 'success',
      title: 'Checkout complete',
      body: 'Your payment completed successfully. Confirming your credit balance now.',
    };
  }

  if (['cancel', 'canceled', 'cancelled'].includes(rawStatus)) {
    return {
      severity: 'warning',
      title: 'Checkout cancelled',
      body: 'No payment was taken. Choose a credit pack when you are ready to top up.',
    };
  }

  if (['failed', 'failure', 'error'].includes(rawStatus)) {
    return {
      severity: 'error',
      title: 'Payment not completed',
      body: 'Stripe did not complete the payment. Try again or contact billing support if the issue continues.',
    };
  }

  return null;
};

const BillingSettingsPage = () => {
  const { overview, loading, error, refresh } = useProfileOverview();
  const [searchParams] = useSearchParams();
  const [portalLoading, setPortalLoading] = useState(false);
  const [portalError, setPortalError] = useState(null);
  const [activePackCheckout, setActivePackCheckout] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [checkoutSync, setCheckoutSync] = useState(null);

  const checkoutAlert = useMemo(() => getCheckoutAlert(searchParams), [searchParams]);
  const checkoutSessionId = useMemo(() => (
    (searchParams.get('session_id') || searchParams.get('sessionId') || '').trim()
  ), [searchParams]);

  useEffect(() => {
    if (!checkoutSessionId || checkoutAlert?.severity !== 'success') {
      return undefined;
    }

    let active = true;
    setCheckoutSync({ status: 'syncing', message: 'Confirming your credit purchase with Stripe...' });

    billingAPI.reconcileCheckoutSession({ sessionId: checkoutSessionId })
      .then((response) => {
        if (!active) {
          return;
        }

        if (response.data?.status === 'paid' && response.data?.applied !== false) {
          setCheckoutSync({ status: 'complete', message: 'Your credits have been added to your wallet.' });
          refresh();
        } else {
          setCheckoutSync({ status: 'pending', message: 'Stripe has not marked this payment as paid yet. Your wallet will update after confirmation.' });
        }
      })
      .catch((err) => {
        if (!active) {
          return;
        }
        setCheckoutSync({
          status: 'error',
          message: err.response?.data?.message || err.message || 'Unable to confirm this checkout session.',
        });
      });

    return () => {
      active = false;
    };
  }, [checkoutAlert?.severity, checkoutSessionId, refresh]);

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 6 }}>
        <Box
          sx={{
            ...surfaceSx,
            py: 8,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <CircularProgress sx={{ color: palette.heroBlueMid }} />
          <Typography sx={{ color: palette.inkSoft, fontFamily: publicBodyFontFamily }}>
            Loading billing details...
          </Typography>
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Alert severity="error" sx={{ mb: 2, borderRadius: '20px' }}>
          <AlertTitle>Unable to load billing settings</AlertTitle>
          {error.response?.data?.message || error.message}
        </Alert>
        <Button sx={publicPrimaryButtonSx} onClick={refresh}>
          Retry
        </Button>
      </Container>
    );
  }

  const billing = overview?.billing || {};
  const usage = overview?.usage;
  const wallet = billing.wallet || {};
  const billingSettings = billing.billingSettings || {};
  const invoices = Array.isArray(billing.invoices) ? billing.invoices : [];
  const supportEmail = billing.supportEmail || 'support@virtualmedic.uk';
  const tokenPacks = (
    Array.isArray(billing.tokenPacks) && billing.tokenPacks.length > 0
      ? billing.tokenPacks
      : billingSettings.tokenPacks
  ) || [];
  const activeTokenPacks = tokenPacks.filter((pack) => pack?.isActive !== false);
  const billingCurrency = (billingSettings.currency || wallet.currency || 'GBP').toUpperCase();
  const historyCostCredits = Number(billingSettings.historyCostTokens ?? billing.historyCostTokens ?? 0);
  const walletBalance = Number(wallet.balance || 0);
  const billingMessages = Array.isArray(billing.messages) ? billing.messages : [];
  const hasCredits = walletBalance > 0;

  const handlePortalLaunch = async () => {
    try {
      setPortalError(null);
      setPortalLoading(true);
      const res = await billingAPI.createPortalSession();
      if (res.data?.url) {
        window.location.href = res.data.url;
      }
    } catch (err) {
      setPortalError(err.response?.data?.message || err.message);
    } finally {
      setPortalLoading(false);
    }
  };

  const handlePackCheckout = async (pack) => {
    try {
      setActionError(null);
      setActivePackCheckout(pack.key);
      const res = await billingAPI.startTokenPackCheckout({ packKey: pack.key });
      const url = res.data?.url || res.data?.checkoutUrl;
      if (url) {
        window.location.href = url;
      }
    } catch (err) {
      setActionError(err.response?.data?.message || err.message);
    } finally {
      setActivePackCheckout(null);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100%',
        bgcolor: palette.heroBlueMist,
        background: 'linear-gradient(180deg, #eef8ff 0%, #f8fcff 48%, #ffffff 100%)',
        py: { xs: 3, md: 5 },
      }}
    >
      <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
        <Box
          sx={{
            position: 'relative',
            overflow: 'hidden',
            mb: 3,
            p: { xs: 3, sm: 3.5, md: 4.5 },
            borderRadius: { xs: '28px', md: '34px' },
            background:
              'linear-gradient(140deg, rgba(18, 61, 103, 0.98) 0%, rgba(21, 83, 129, 0.97) 54%, rgba(49, 182, 228, 0.93) 100%)',
            color: palette.white,
            boxShadow: '0 34px 84px rgba(18, 61, 103, 0.16)',
          }}
        >
          <Box
            component="img"
            src={marketingSymbols.silhouette}
            alt=""
            aria-hidden
            sx={{
              position: 'absolute',
              right: { xs: -20, md: 28 },
              top: { xs: -34, md: -56 },
              width: { xs: 180, md: 280 },
              opacity: 0.14,
              pointerEvents: 'none',
            }}
          />

          <Grid container spacing={3} alignItems="center" sx={{ position: 'relative', zIndex: 1 }}>
            <Grid item xs={12} lg={7}>
              <Stack spacing={1.4}>
                <Chip
                  icon={<WalletCards size={18} />}
                  label="Billing and credits"
                  sx={{
                    alignSelf: 'flex-start',
                    bgcolor: alpha('#ffffff', 0.12),
                    color: palette.white,
                    borderRadius: '999px',
                    fontWeight: 800,
                    fontFamily: publicBodyFontFamily,
                    '& .MuiChip-icon': { color: 'inherit' },
                  }}
                />
                <Typography
                  component="h1"
                  sx={{
                    fontFamily: publicHeadingFontFamily,
                    fontSize: { xs: '2.2rem', md: '3rem' },
                    lineHeight: 1.02,
                    letterSpacing: '-0.04em',
                  }}
                >
                  Manage your credit wallet.
                </Typography>
                <Typography
                  sx={{
                    maxWidth: 720,
                    color: alpha('#ffffff', 0.84),
                    fontSize: { xs: '1rem', md: '1.08rem' },
                    lineHeight: 1.72,
                    fontFamily: publicBodyFontFamily,
                  }}
                >
                  Top up for completed consultation cases and open Stripe billing records.
                </Typography>
              </Stack>
            </Grid>

            <Grid item xs={12} lg={5}>
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 1.4,
                  width: { xs: '100%', sm: 'auto' },
                  p: { xs: 2, sm: 2.2 },
                  borderRadius: '24px',
                  bgcolor: alpha('#ffffff', 0.12),
                  border: `1px solid ${alpha('#ffffff', 0.16)}`,
                }}
              >
                <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 46, height: 46, borderRadius: '16px', bgcolor: alpha('#ffffff', 0.14) }}>
                  <WalletCards size={24} />
                </Box>
                <Typography sx={{ color: palette.white, fontSize: { xs: '1.35rem', sm: '1.55rem' }, lineHeight: 1.08, fontFamily: publicHeadingFontFamily }}>
                  {walletBalance.toLocaleString()} credits left
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>

        <Stack spacing={1.5} sx={{ mb: 3 }}>
          {checkoutAlert ? (
            <Alert severity={checkoutAlert.severity} sx={{ borderRadius: '20px' }}>
              <AlertTitle>{checkoutAlert.title}</AlertTitle>
              {checkoutAlert.body}
            </Alert>
          ) : null}

          {checkoutSync ? (
            <Alert
              severity={checkoutSync.status === 'error' ? 'error' : (checkoutSync.status === 'pending' ? 'warning' : 'success')}
              sx={{ borderRadius: '20px' }}
            >
              {checkoutSync.message}
            </Alert>
          ) : null}

          {!billing.featureEnabled ? (
            <Alert severity="info" sx={{ borderRadius: '20px' }}>
              <AlertTitle>Billing is not fully enabled</AlertTitle>
              Stripe billing is not active for this environment yet.
            </Alert>
          ) : null}

          {actionError ? (
            <Alert severity="error" sx={{ borderRadius: '20px' }}>
              <AlertTitle>Checkout unavailable</AlertTitle>
              {actionError}
            </Alert>
          ) : null}

          {portalError ? (
            <Alert severity="error" sx={{ borderRadius: '20px' }}>
              <AlertTitle>Unable to open Stripe portal</AlertTitle>
              {portalError}
            </Alert>
          ) : null}

          {billingMessages.slice(0, 3).map((message) => (
            <Alert key={message.id || message.title} severity={message.severity || 'info'} sx={{ borderRadius: '20px' }}>
              <AlertTitle>{message.title}</AlertTitle>
              {message.body}
            </Alert>
          ))}
        </Stack>

        <Grid container spacing={3}>
          <Grid item xs={12} lg={4}>
            <Stack spacing={3}>
              <Box sx={{ ...surfaceSx, p: { xs: 3, md: 3.5 } }}>
                <Stack spacing={2.2}>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 50, height: 50, borderRadius: '18px', bgcolor: alpha(palette.heroBlue, 0.16), color: palette.heroBlueMid }}>
                      <WalletCards size={25} />
                    </Box>
                    <Box>
                      <Typography sx={{ color: palette.ink, fontWeight: 900, fontSize: '1.2rem', fontFamily: publicBodyFontFamily }}>
                        Wallet balance
                      </Typography>
                      <Typography sx={{ color: palette.inkSoft, fontFamily: publicBodyFontFamily }}>
                        {hasCredits ? `${walletBalance.toLocaleString()} credits left` : 'No credits remaining'}
                      </Typography>
                    </Box>
                  </Stack>

                  <Button
                    onClick={handlePortalLaunch}
                    disabled={portalLoading}
                    startIcon={<CreditCard size={18} />}
                    sx={{ ...publicSecondaryButtonSx, justifyContent: 'center' }}
                  >
                    {portalLoading ? 'Opening Stripe...' : 'Manage payment method'}
                  </Button>
                </Stack>
              </Box>

              <Box sx={{ ...surfaceSx, p: { xs: 3, md: 3.5 } }}>
                <Stack spacing={1.6}>
                  <Stack direction="row" spacing={1.4} alignItems="center">
                    <HelpCircle size={22} color={palette.heroBlueMid} />
                    <Typography sx={{ color: palette.ink, fontWeight: 900, fontSize: '1.1rem', fontFamily: publicBodyFontFamily }}>
                      Billing support
                    </Typography>
                  </Stack>
                  <Typography sx={{ color: palette.inkSoft, lineHeight: 1.7, fontFamily: publicBodyFontFamily }}>
                    Refund requests must be submitted within {billingSettings.refundWindowDays ?? 14} days and before credits are consumed.
                  </Typography>
                  <MuiLink href={`mailto:${supportEmail}`} sx={{ color: palette.heroBlueMid, fontWeight: 800, textDecoration: 'none', fontFamily: publicBodyFontFamily }}>
                    {supportEmail}
                  </MuiLink>
                </Stack>
              </Box>
            </Stack>
          </Grid>

          <Grid item xs={12} lg={8}>
            <Stack spacing={3}>
              <Box sx={{ ...surfaceSx, p: { xs: 3, md: 3.5 } }}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} sx={{ mb: 2.5 }}>
                  <Box>
                    <Typography sx={{ mb: 0.8, color: palette.heroBlueMid, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', fontSize: '0.78rem', fontFamily: publicBodyFontFamily }}>
                      Credit packs
                    </Typography>
                    <Typography sx={{ color: palette.ink, fontSize: { xs: '1.55rem', md: '1.85rem' }, lineHeight: 1.08, fontFamily: publicHeadingFontFamily }}>
                      Top up for future cases
                    </Typography>
                  </Box>
                  <Chip
                    icon={<ShieldCheck size={16} />}
                    label="Secure checkout by Stripe"
                    sx={{
                      ...statusChipSx,
                      bgcolor: alpha(palette.heroBlueMid, 0.10),
                      color: palette.heroBlueDeep,
                      '& .MuiChip-icon': { color: 'inherit' },
                    }}
                  />
                </Stack>

                {activeTokenPacks.length > 0 ? (
                  <Grid container spacing={2}>
                    {activeTokenPacks.map((pack) => {
                      const casesIncluded = Number(pack.historiesEquivalent)
                        || (historyCostCredits > 0 ? Math.floor(Number(pack.tokens || 0) / historyCostCredits) : 0);
                      const disabled = activePackCheckout === pack.key || !pack.stripePriceId;
                      const isLoadingPack = activePackCheckout === pack.key;

                      return (
                        <Grid item xs={12} md={4} key={pack.key}>
                          <Box sx={{ ...insetSx, p: 2.4, height: '100%', display: 'flex', flexDirection: 'column', bgcolor: '#ffffff' }}>
                            <Stack spacing={1.2} sx={{ flex: 1 }}>
                              <Typography sx={{ color: palette.ink, fontSize: '1.18rem', fontWeight: 900, fontFamily: publicBodyFontFamily }}>
                                {pack.name}
                              </Typography>
                              <Typography sx={{ color: palette.ink, fontSize: { xs: '1.75rem', md: '2rem' }, lineHeight: 1, fontFamily: publicHeadingFontFamily }}>
                                {formatCurrency(pack.priceCents, (pack.currency || billingCurrency).toUpperCase())}
                              </Typography>
                              <Typography sx={{ color: palette.inkSoft, lineHeight: 1.6, fontFamily: publicBodyFontFamily }}>
                                {casesIncluded.toLocaleString()} {pluralise(casesIncluded, 'case')} worth of practice, delivered as {Number(pack.tokens || 0).toLocaleString()} credits.
                              </Typography>
                            </Stack>

                            <Tooltip title={!pack.stripePriceId ? 'Add this Stripe price ID in global admin before purchase.' : ''}>
                              <span>
                                <Button
                                  fullWidth
                                  disabled={disabled}
                                  onClick={() => handlePackCheckout(pack)}
                                  endIcon={<ArrowRight size={18} />}
                                  sx={{ ...publicPrimaryButtonSx, mt: 2.4, minHeight: 48 }}
                                >
                                  {isLoadingPack ? 'Redirecting...' : (!pack.stripePriceId ? 'Price ID needed' : 'Buy credits')}
                                </Button>
                              </span>
                            </Tooltip>
                          </Box>
                        </Grid>
                      );
                    })}
                  </Grid>
                ) : (
                  <Alert severity="warning" sx={{ borderRadius: '20px' }}>
                    <AlertTitle>No credit packs configured</AlertTitle>
                    Add active credit packs in global admin before users can check out.
                  </Alert>
                )}
              </Box>

              <Grid container spacing={3}>
                <Grid item xs={12} md={5}>
                  <Box sx={{ ...surfaceSx, p: { xs: 3, md: 3.5 }, height: '100%' }}>
                    <Stack direction="row" spacing={1.4} alignItems="center" sx={{ mb: 2 }}>
                      <CheckCircle2 size={22} color={palette.heroBlueMid} />
                      <Typography sx={{ color: palette.ink, fontWeight: 900, fontSize: '1.1rem', fontFamily: publicBodyFontFamily }}>
                        Usage snapshot
                      </Typography>
                    </Stack>
                    <Stack spacing={1.4}>
                      <Box sx={{ ...insetSx, p: 2 }}>
                        <Typography sx={{ color: palette.inkSoft, fontSize: '0.8rem', fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', fontFamily: publicBodyFontFamily }}>
                          Completed sessions
                        </Typography>
                        <Typography sx={{ mt: 0.75, color: palette.ink, fontSize: '1.55rem', fontFamily: publicHeadingFontFamily }}>
                          {(usage?.totalSessions || 0).toLocaleString()}
                        </Typography>
                      </Box>
                      <Box sx={{ ...insetSx, p: 2 }}>
                        <Typography sx={{ color: palette.inkSoft, fontSize: '0.8rem', fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', fontFamily: publicBodyFontFamily }}>
                          Average score
                        </Typography>
                        <Typography sx={{ mt: 0.75, color: palette.ink, fontSize: '1.55rem', fontFamily: publicHeadingFontFamily }}>
                          {formatAverageScore(usage?.averageScore)}{Number.isFinite(Number(usage?.averageScore)) ? '%' : ''}
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>
                </Grid>

                <Grid item xs={12} md={7}>
                  <Box sx={{ ...surfaceSx, p: { xs: 3, md: 3.5 }, height: '100%' }}>
                    <Stack direction="row" spacing={1.4} alignItems="center" sx={{ mb: 2 }}>
                      <ReceiptText size={22} color={palette.heroBlueMid} />
                      <Typography sx={{ color: palette.ink, fontWeight: 900, fontSize: '1.1rem', fontFamily: publicBodyFontFamily }}>
                        Recent invoices
                      </Typography>
                    </Stack>

                    {invoices.length > 0 ? (
                      <Stack spacing={1.2}>
                        {invoices.slice(0, 5).map((invoice) => {
                          const status = (invoice.status || 'recorded').replace(/_/g, ' ');
                          const amount = invoice.amountPaid || invoice.amountDue || 0;
                          const invoiceCurrency = (invoice.currency || billingCurrency).toUpperCase();

                          return (
                            <Box key={invoice.invoiceId || `${invoice.recordedAt}-${amount}`} sx={{ ...insetSx, p: 1.8, bgcolor: '#ffffff' }}>
                              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.4} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }}>
                                <Box>
                                  <Typography sx={{ color: palette.ink, fontWeight: 900, fontFamily: publicBodyFontFamily }}>
                                    {formatCurrency(amount, invoiceCurrency)}
                                  </Typography>
                                  <Typography sx={{ color: palette.inkSoft, fontSize: '0.9rem', fontFamily: publicBodyFontFamily }}>
                                    {formatDateTime(invoice.paidAt || invoice.recordedAt || invoice.dueDate)}
                                  </Typography>
                                </Box>
                                <Stack direction="row" spacing={1} alignItems="center">
                                  <Chip label={status} size="small" sx={{ ...statusChipSx, bgcolor: alpha(palette.heroBlueMid, 0.10), color: palette.heroBlueDeep }} />
                                  {invoice.hostedInvoiceUrl ? (
                                    <MuiLink href={invoice.hostedInvoiceUrl} target="_blank" rel="noopener" sx={{ display: 'inline-flex', color: palette.heroBlueMid }}>
                                      <ExternalLink size={18} />
                                    </MuiLink>
                                  ) : null}
                                </Stack>
                              </Stack>
                            </Box>
                          );
                        })}
                      </Stack>
                    ) : (
                      <Typography sx={{ color: palette.inkSoft, fontFamily: publicBodyFontFamily }}>
                        No invoices yet.
                      </Typography>
                    )}
                  </Box>
                </Grid>
              </Grid>

            </Stack>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default BillingSettingsPage;
